:where(button, a) {
  all: unset;
  display: inline-flex;
  align-items: center;
  user-select: none;
  touch-action: manipulation;
  cursor: pointer;
}

button:disabled {
  pointer-events: none;
}

*,
::after,
::before {
  box-sizing: border-box;
}

.caption,
dialog {
  background-color: unset;
  border: none;
  color: inherit;
  font: inherit;
  margin: 0;
  padding: 0;
  outline: 0;
}

::backdrop {
  background: unset;
  pointer-events: none;
}

:host {
  /**
   * @prop --ch-dialog-block-start:
   * This specifies the value used in calculating the dialog's position along the y-axis. The default value centers the dialog along the y-axis.
   * @default calc(50dvh - 50%)
   */
  --ch-dialog-block-start: calc(50dvh - 50%);
  /**
   * @prop --ch-dialog-inline-start:
   * This specifies the value used in calculating the dialog's position along the x-axis. The default value centers the dialog along the x-axis.
   * @default calc(50dvw - 50%);
   */
  --ch-dialog-inline-start: calc(50dvw - 50%);
  /**
   * @prop --ch-dialog-block-size:
   * Specifies the block size of the dialog. Useful for scenarios where the
   * dialog is resizable.
   * @default max-content
   */
  --ch-dialog-block-size: max-content;
  /**
    * @prop --ch-dialog-inline-size:
    * Specifies the inline size of the dialog. Useful for scenarios where the
    * dialog is resizable.
    * @default max-content
    */
  --ch-dialog-inline-size: max-content;
  /**
    * @prop --ch-dialog-max-block-size:
    * Specifies the maximum block size of the dialog. Useful for scenarios where the
    * dialog is resizable.
    * @default auto
    */
  --ch-dialog-max-block-size: auto;
  /**
      * @prop --ch-dialog-max-inline-size:
      * Specifies the maximum inline size of the dialog. Useful for scenarios
      * where the dialog is resizable.
      * @default auto
      */
  --ch-dialog-max-inline-size: auto;
  /**
    * @prop --ch-dialog-min-block-size:
    * Specifies the minimum block size of the dialog. Useful for scenarios where the
    * dialog is resizable.
    * @default auto
    */
  --ch-dialog-min-block-size: auto;
  /**
     * @prop --ch-dialog-min-inline-size:
     * Specifies the minimum inline size of the dialog. Useful for scenarios
     * where the dialog is resizable.
     * @default auto
     */
  --ch-dialog-min-inline-size: auto;
  /**
    * @prop --ch-dialog-resize-threshold:
    * Specifies the size of the threshold to resize the dialog.
    * @default 8px
    */
  --ch-dialog-resize-threshold: 8px;
  --ch-dialog-resize-threshold--half-negative: calc(
    var(--ch-dialog-resize-threshold) * -0.5
  );
  --ch-dialog-border-inline-start-width: 0px;
  --ch-dialog-border-inline-end-width: 0px;
  --ch-dialog-border-block-start-width: 0px;
  --ch-dialog-border-block-end-width: 0px;
  --ch-dialog-resize__inline-start: calc(
    var(--ch-dialog-resize-threshold--half-negative) -
      var(--ch-dialog-border-inline-start-width) * 0.5
  );
  --ch-dialog-resize__inline-end: calc(
    var(--ch-dialog-resize-threshold--half-negative) -
      var(--ch-dialog-border-inline-end-width) * 0.5
  );
  --ch-dialog-resize__block-start: calc(
    var(--ch-dialog-resize-threshold--half-negative) -
      var(--ch-dialog-border-block-start-width) * 0.5
  );
  --ch-dialog-resize__block-end: calc(
    var(--ch-dialog-resize-threshold--half-negative) -
      var(--ch-dialog-border-block-end-width) * 0.5
  );
  --ch-dialog-dragged-x: 0px;
  --ch-dialog-dragged-y: 0px;
  --ch-dialog-rtl: 1;
}

:host(.ch-dialog--modal) {
  position: fixed;
  inset: 0;
}

:host(.ch-dialog--non-modal) {
  display: contents;
}

:host(.ch-dialog--hidden) {
  display: none;
}

.header {
  display: grid;
  grid-template: "caption close-button" 1fr/1fr max-content;
  align-items: center;
}

.caption {
  grid-area: caption;
}

.close-button {
  grid-area: close-button;
}

dialog {
  display: flex;
  flex-direction: column;
  margin: unset;
  position: fixed;
  overflow: visible;
  inline-size: var(--ch-dialog-inline-size);
  block-size: var(--ch-dialog-block-size);
  min-inline-size: var(--ch-dialog-min-inline-size);
  min-block-size: var(--ch-dialog-min-block-size);
  max-inline-size: var(--ch-dialog-max-inline-size);
  max-block-size: var(--ch-dialog-max-block-size);
  inset-block-start: 0;
  inset-inline-start: 0;
  translate: calc((var(--ch-dialog-inline-start) + var(--ch-dialog-dragged-x)) * var(--ch-dialog-rtl)) calc(var(--ch-dialog-block-start) + var(--ch-dialog-dragged-y));
}

.content {
  display: grid;
  flex: 1;
}

.footer {
  display: flex;
  justify-content: flex-end;
  margin-block-start: auto;
}

.gx-dialog-dragging {
  cursor: move;
}
.gx-dialog-dragging ::slotted(*) {
  pointer-events: none !important;
  user-select: none;
}

:host(.ch-dialog-resizing) {
  pointer-events: none;
  user-select: none;
}

.edge__block-start, .edge__block-end {
  position: fixed;
  inset-inline: min(0px, (var(--ch-dialog-resize-threshold) - var(--ch-dialog-border-inline-start-width)) / 2) min(0px, (var(--ch-dialog-resize-threshold) - var(--ch-dialog-border-inline-end-width)) / 2);
  block-size: var(--ch-dialog-resize-threshold);
  cursor: ns-resize;
}
.edge__inline-start, .edge__inline-end {
  position: fixed;
  inset-block: min(0px, (var(--ch-dialog-resize-threshold) - var(--ch-dialog-border-block-start-width)) / 2) min(0px, (var(--ch-dialog-resize-threshold) - var(--ch-dialog-border-block-end-width)) / 2);
  inline-size: var(--ch-dialog-resize-threshold);
  cursor: ew-resize;
}
.edge__block-start {
  inset-block-start: var(--ch-dialog-resize__block-start);
}
.edge__block-end {
  inset-block-end: var(--ch-dialog-resize__block-end);
}
.edge__inline-start {
  inset-inline-start: var(--ch-dialog-resize__inline-start);
}
.edge__inline-end {
  inset-inline-end: var(--ch-dialog-resize__inline-end);
}

.corner__block-start-inline-start, .corner__block-end-inline-end {
  position: fixed;
  block-size: var(--ch-dialog-resize-threshold);
  inline-size: var(--ch-dialog-resize-threshold);
  cursor: nwse-resize;
}
.corner__block-start-inline-end, .corner__block-end-inline-start {
  position: fixed;
  block-size: var(--ch-dialog-resize-threshold);
  inline-size: var(--ch-dialog-resize-threshold);
  cursor: nesw-resize;
}
.corner__block-start-inline-start {
  inset-block-start: var(--ch-dialog-resize__block-start);
  inset-inline-start: var(--ch-dialog-resize__inline-start);
}
.corner__block-end-inline-end {
  inset-block-end: var(--ch-dialog-resize__block-end);
  inset-inline-end: var(--ch-dialog-resize__inline-end);
}
.corner__block-start-inline-end {
  inset-block-start: var(--ch-dialog-resize__block-start);
  inset-inline-end: var(--ch-dialog-resize__inline-end);
}
.corner__block-end-inline-start {
  inset-block-end: var(--ch-dialog-resize__block-end);
  inset-inline-start: var(--ch-dialog-resize__inline-start);
}

:host(.ch-dialog-rtl) .corner__block-start-inline-start,
:host(.ch-dialog-rtl) .corner__block-end-inline-end {
  cursor: nesw-resize;
}

:host(.ch-dialog-rtl) .corner__block-start-inline-end,
:host(.ch-dialog-rtl) .corner__block-end-inline-start {
  cursor: nwse-resize;
}

.resize-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
}